import { Suspense } from "react";
import { useInView } from "react-cool-inview";
import { useTranslation } from "react-i18next";
import {
  Box,
  Drawer,
  DrawerProps,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import Button from "components/shared/Button";
import Loading from "components/shared/Loading";
import useGetNotificationsForUser from "hooks/api/REST/notifications/useGetNotificationsForUser";
import useMarkAllNotifications from "hooks/api/REST/notifications/useMarkAllNotifications";
import { NOTIFICATIONS_CURRENT_USER_ID } from "utils/constants/api.constants";
import NotificationsListItem from "./NotificationsListItem";
import { CloseIcon } from "assets/icons";
import styles from "./Notifications.styles";

type TNotificationsDrawerProps = Omit<
  DrawerProps,
  "anchor" | "onClose" | "sx" | "variant"
> & {
  onClose?: () => void;
  isOpen: boolean;
};

const NOTIFICATIONS_SIZE = 10;

const Notifications = ({
  onClose,
  isOpen,
  ...props
}: TNotificationsDrawerProps) => {
  const { t } = useTranslation("common", {
    keyPrefix: "navigation.notificationsMetaMessages",
  });

  const { notifications, fetchNextPage, hasNextPage } =
    useGetNotificationsForUser(NOTIFICATIONS_CURRENT_USER_ID, {
      size: NOTIFICATIONS_SIZE,
    });

  const { markAllNotificationsRead } = useMarkAllNotifications();

  const { observe } = useInView({
    onEnter: fetchNextPage,
  });

  return (
    <Drawer
      anchor="left"
      variant="persistent"
      role="alertdialog"
      aria-label={t("title")}
      sx={styles.drawer}
      onClose={onClose}
      open={isOpen}
      {...props}
    >
      <Stack direction="column" sx={styles.container}>
        <Stack direction="row" spacing={1} sx={styles.headerContainer}>
          <Typography variant="h6" data-qaid="notifications-list-title">
            {t("title")}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: "center",
            }}
          >
            <Button
              aria-label={t("markAllButtonAriaLabel")}
              variant="text"
              onClick={markAllNotificationsRead}
              size="small"
              data-qaid="notifications-list-mark-all-as-read-button"
            >
              {t("markAllButton")}
            </Button>
            <IconButton
              aria-label={t("closeXButtonAriaLabel")}
              onClick={onClose}
              size="small"
              data-qaid="notifications-list-panel-close-button"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Box sx={styles.notificationsListContainer}>
          <List disablePadding data-qaid="notification-items-list">
            {notifications?.map((notificationItem) => (
              <ListItem
                key={notificationItem.id}
                disablePadding
                data-qaid="notifications-list-item"
              >
                <Suspense fallback={null}>
                  <NotificationsListItem data={notificationItem} />
                </Suspense>
              </ListItem>
            ))}
            {hasNextPage && (
              <ListItem sx={styles.loadingItem} ref={observe}>
                <Loading />
              </ListItem>
            )}
          </List>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default Notifications;
